import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import type { RootState } from '../../store';
import type { UserProfile } from '../../types/auth';
import { clearAuth } from '../../store/slices/authSlice';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { usePermissions } from '../../hooks/usePermissions';
import { isTokenExpired } from '../../utils/tokenUtils';
import { buildLoginRedirectPath, SESSION_EXPIRED_MESSAGE } from '../../utils/authSession';

interface ProtectedRouteProps {
  children: React.ReactNode;
  guardType?: 'public' | 'authenticated' | 'admin' | 'superuser';
  profiles?: UserProfile[]; // Specific profiles that can access
  permissions?: string[]; // Specific permissions required
  requiresPatientContext?: boolean;
  requiresStudyContext?: boolean;
  requireAll?: boolean; // If true, user must have ALL permissions
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  guardType = 'authenticated',
  profiles = [],
  permissions = [],
  requiresPatientContext = false,
  requiresStudyContext = false,
  requireAll = false
}) => {
  const dispatch = useDispatch();
  const {
    isAuthenticated,
    isLoading,
    token,
    user,
  } = useSelector((state: RootState) => state.auth);
  
  const { 
    hasPermission, 
    canAccessRoute: userCanAccessRoute 
  } = usePermissions();
  
  const location = useLocation();
  const params = useParams();

  const tokenExpired = Boolean(token && isTokenExpired(token, 0));

  useEffect(() => {
    if (tokenExpired) {
      dispatch(clearAuth());
    }
  }, [tokenExpired, dispatch]);

  // Check if route should be public
  const isPublicRoute = guardType === 'public';

  // Validate patient context if required
  const hasValidPatientContext = useMemo(() => {
    if (!requiresPatientContext) return true;
    return Boolean(params.patientId);
  }, [requiresPatientContext, params.patientId]);

  // Validate study context if required
  const hasValidStudyContext = useMemo(() => {
    if (!requiresStudyContext) return true;
    return Boolean(params.studyId);
  }, [requiresStudyContext, params.studyId]);

  // Check profile-based authorization
  const isProfileAuthorized = useMemo(() => {
    if (profiles.length === 0) return true;
    if (!user) return false;
    
    // Superusers can access any profile-restricted content
    if (user.is_superuser || user.profile === 'superadmin') {
      return true;
    }
    
    return profiles.includes(user.profile);
  }, [user, profiles]);

  // Check permission-based authorization
  const hasRequiredPermissions = useMemo(() => {
    if (permissions.length === 0) return true;
    
    // Superusers have all permissions
    if (user?.is_superuser || user?.profile === 'superadmin') {
      return true;
    }
    
    const permissionChecks = permissions.map(permission => hasPermission(permission));
    return requireAll 
      ? permissionChecks.every(check => check)
      : permissionChecks.some(check => check);
  }, [permissions, hasPermission, requireAll, user]);

  // Check legacy guard type authorization
  const isGuardTypeAuthorized = useMemo(() => {
    if (!user) return false;
    
    switch (guardType) {
      case 'superuser':
        return user.profile === 'superadmin' || user.is_superuser === true;
      case 'admin':
        return user.profile === 'superadmin' || user.profile === 'institution-admin' || user.is_superuser === true;
      case 'authenticated':
        return true;
      default:
        return true;
    }
  }, [user, guardType]);

  // Check if user can access current route
  const canAccessCurrentRoute = useMemo(() => {
    // Superusers can access any route
    if (user?.is_superuser || user?.profile === 'superadmin') {
      return true;
    }
    
    return userCanAccessRoute(location.pathname);
  }, [user, userCanAccessRoute, location.pathname]);

  // Show loading spinner during authentication check
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-medical-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-medical-600">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Handle public routes
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Reject expired tokens even when isAuthenticated is true from stale storage
  if (tokenExpired) {
    return (
      <Navigate
        to={buildLoginRedirectPath(location.pathname, location.search)}
        state={{ from: location, message: SESSION_EXPIRED_MESSAGE }}
        replace
      />
    );
  }

  // Check authentication requirement
  if (!isAuthenticated) {
    return (
      <Navigate
        to={buildLoginRedirectPath(location.pathname, location.search)}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check email verification requirement
  if (user && !user.is_verified) {
    return (
      <Navigate 
        to="/auth/verify-email" 
        state={{ 
          from: location,
          email: user.email,
          username: user.username,
          message: 'Verificación de email requerida para acceder a esta sección'
        }} 
        replace 
      />
    );
  }

  // Check profile authorization
  if (!isProfileAuthorized) {
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ 
          from: location,
          message: 'Tu perfil no tiene acceso a esta sección'
        }} 
        replace 
      />
    );
  }

  // Check permission authorization
  if (!hasRequiredPermissions) {
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ 
          from: location,
          message: 'No tienes los permisos necesarios para acceder a esta sección'
        }} 
        replace 
      />
    );
  }

  // Check legacy guard type authorization
  if (!isGuardTypeAuthorized) {
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ 
          from: location,
          message: 'No tienes permisos suficientes para acceder a esta sección'
        }} 
        replace 
      />
    );
  }

  // Check route-based access control
  if (!canAccessCurrentRoute) {
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ 
          from: location,
          message: 'No tienes acceso a esta ruta'
        }} 
        replace 
      />
    );
  }

  // Check patient context requirement
  if (!hasValidPatientContext) {
    return (
      <Navigate 
        to="/patients" 
        state={{ 
          from: location,
          message: 'Selecciona un paciente para continuar'
        }} 
        replace 
      />
    );
  }

  // Check study context requirement  
  if (!hasValidStudyContext) {
    return (
      <Navigate 
        to={`/patient/${params.patientId}/studies`} 
        state={{ 
          from: location,
          message: 'Selecciona un estudio para continuar'
        }} 
        replace 
      />
    );
  }

  return <>{children}</>;
};
